'use client';

import { Box, Button, Center, HStack, IconButton, Input, Text, VStack } from '@chakra-ui/react';
import { ExternalLink, MessageSquare, RefreshCw } from 'lucide-react';
import NextLink from 'next/link';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { SidePanelToggleButton } from '@/components/core/app-layout/SidePanelToggleButton';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { formatCodexRelativeTime } from '@/lib/codex-workspace-format';
import type { ReportCommentThread, ReportSummary } from '@/lib/reports/types';

export interface ProjectCommentDocument {
  report: ReportSummary;
  comments: ReportCommentThread[];
}

interface ProjectCommentsInboxProps {
  documents: ProjectCommentDocument[];
  loading: boolean;
  refreshing: boolean;
  onClose: () => void;
  onRefresh: () => void;
  onReply: (reportId: string, commentId: string, body: string) => Promise<void>;
  onResolve: (reportId: string, commentId: string) => Promise<void>;
}

export function ProjectCommentsInbox({
  documents,
  loading,
  refreshing,
  onClose,
  onRefresh,
  onReply,
  onResolve,
}: ProjectCommentsInboxProps) {
  const [showResolved, setShowResolved] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [savingCommentId, setSavingCommentId] = useState<string | null>(null);
  const [resolvingCommentId, setResolvingCommentId] = useState<string | null>(null);
  const unresolvedCount = useMemo(
    () =>
      documents.reduce(
        (total, document) =>
          total +
          document.comments.reduce((count, comment) => count + (comment.resolved ? 0 : 1), 0),
        0
      ),
    [documents]
  );
  const visibleDocuments = useMemo(
    () =>
      documents
        .map(document => ({
          ...document,
          comments: document.comments.filter(comment => showResolved || !comment.resolved),
        }))
        .filter(document => document.comments.length > 0),
    [documents, showResolved]
  );

  return (
    <VStack align="stretch" gap="0" h="full" minH={0} bg="ui.surface.rail">
      <HStack
        minH="54px"
        px="3"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
        flexShrink={0}
      >
        <MessageSquare size={17} />
        <Text fontSize="sm" fontWeight="750">
          Comments
        </Text>
        <Text fontSize="2xs" color="ui.text.tertiary">
          {unresolvedCount || 'No'} open
        </Text>
        <Box flex="1" />
        <IconButton
          aria-label="Refresh comments"
          title="Refresh comments"
          size="sm"
          variant="ghost"
          loading={refreshing}
          onClick={onRefresh}
        >
          <RefreshCw size={15} />
        </IconButton>
        <SidePanelToggleButton open label="Collapse comments" onClick={onClose} />
      </HStack>

      <HStack px="3" py="2" borderBottomWidth="1px" borderColor="ui.border.subtle">
        <Button
          size="xs"
          variant={showResolved ? 'ghost' : 'subtle'}
          onClick={() => setShowResolved(false)}
        >
          Open {unresolvedCount ? `(${unresolvedCount})` : ''}
        </Button>
        <Button
          size="xs"
          variant={showResolved ? 'subtle' : 'ghost'}
          onClick={() => setShowResolved(true)}
        >
          All
        </Button>
      </HStack>

      <Box flex="1" minH={0} overflowY="auto" overscrollBehavior="contain" p="2.5">
        {loading ? (
          <Center minH="240px">
            <InlineSpinner size="md" />
          </Center>
        ) : visibleDocuments.length ? (
          <VStack align="stretch" gap="4">
            {visibleDocuments.map(document => (
              <Box key={document.report.id}>
                <HStack px="1" mb="1.5" minW={0}>
                  <Text fontSize="xs" fontWeight="750" truncate minW={0} flex="1">
                    {document.report.title}
                  </Text>
                  <IconButton
                    asChild
                    aria-label={`Open ${document.report.title} in full page`}
                    title="Open full page"
                    size="2xs"
                    variant="ghost"
                  >
                    <NextLink href={`/app/docs/${encodeURIComponent(document.report.id)}`}>
                      <ExternalLink size={13} />
                    </NextLink>
                  </IconButton>
                </HStack>
                <VStack align="stretch" gap="2">
                  {document.comments.map(comment => {
                    const selected = selectedCommentId === comment.id;
                    const firstMessage = comment.messages[0];
                    const draft = replyDrafts[comment.id] || '';
                    return (
                      <Box
                        key={comment.id}
                        borderWidth="1px"
                        borderColor={selected ? 'ui.border.strong' : 'ui.border.subtle'}
                        borderRadius="glassInput"
                        bg={selected ? 'ui.surface.selected' : 'ui.surface.card'}
                        overflow="hidden"
                      >
                        <Box asChild w="full" p="3" textAlign="left">
                          <button
                            type="button"
                            onClick={() => setSelectedCommentId(selected ? null : comment.id)}
                          >
                            <HStack justify="space-between" gap="2">
                              <Text fontSize="xs" fontWeight="750">
                                {firstMessage?.authorName || 'Collaborator'}
                              </Text>
                              <Text fontSize="2xs" color="ui.text.tertiary">
                                {formatCodexRelativeTime(comment.updatedAt)}
                              </Text>
                            </HStack>
                            <Text mt="1" fontSize="xs" color="ui.text.secondary" lineClamp={3}>
                              {firstMessage?.body}
                            </Text>
                            {comment.messages.length > 1 ? (
                              <Text mt="1.5" fontSize="2xs" color="ui.text.tertiary">
                                {comment.messages.length - 1}{' '}
                                {comment.messages.length === 2 ? 'reply' : 'replies'}
                              </Text>
                            ) : null}
                          </button>
                        </Box>

                        {selected ? (
                          <VStack
                            align="stretch"
                            gap="2.5"
                            px="3"
                            pb="3"
                            borderTopWidth="1px"
                            borderColor="ui.border.subtle"
                          >
                            {comment.messages.slice(1).map(message => (
                              <Box key={message.id} pt="2.5">
                                <HStack justify="space-between" gap="2">
                                  <Text fontSize="2xs" fontWeight="750">
                                    {message.authorName}
                                  </Text>
                                  <Text fontSize="2xs" color="ui.text.tertiary">
                                    {formatCodexRelativeTime(message.createdAt)}
                                  </Text>
                                </HStack>
                                <Text mt="0.5" fontSize="xs" color="ui.text.secondary">
                                  {message.body}
                                </Text>
                              </Box>
                            ))}
                            {!comment.resolved ? (
                              <HStack gap="1.5" pt="2.5">
                                <Input
                                  size="xs"
                                  aria-label="Reply to comment"
                                  value={draft}
                                  placeholder="Reply…"
                                  onChange={event =>
                                    setReplyDrafts(current => ({
                                      ...current,
                                      [comment.id]: event.target.value,
                                    }))
                                  }
                                />
                                <Button
                                  size="xs"
                                  loading={savingCommentId === comment.id}
                                  disabled={!draft.trim()}
                                  onClick={() => {
                                    setSavingCommentId(comment.id);
                                    void onReply(document.report.id, comment.id, draft.trim())
                                      .then(() => {
                                        setReplyDrafts(current => ({
                                          ...current,
                                          [comment.id]: '',
                                        }));
                                      })
                                      .catch(error => {
                                        toast.error(
                                          error instanceof Error ? error.message : 'Failed to reply'
                                        );
                                      })
                                      .finally(() => setSavingCommentId(null));
                                  }}
                                >
                                  Reply
                                </Button>
                              </HStack>
                            ) : null}
                            {!comment.resolved ? (
                              <Button
                                size="xs"
                                variant="ghost"
                                alignSelf="flex-end"
                                color="ui.text.tertiary"
                                loading={resolvingCommentId === comment.id}
                                onClick={() => {
                                  setResolvingCommentId(comment.id);
                                  void onResolve(document.report.id, comment.id)
                                    .catch(error => {
                                      toast.error(
                                        error instanceof Error
                                          ? error.message
                                          : 'Failed to resolve comment'
                                      );
                                    })
                                    .finally(() => setResolvingCommentId(null));
                                }}
                              >
                                Resolve thread
                              </Button>
                            ) : (
                              <Text fontSize="2xs" color="ui.text.tertiary" alignSelf="flex-end">
                                Resolved
                              </Text>
                            )}
                          </VStack>
                        ) : null}
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            ))}
          </VStack>
        ) : (
          <Center minH="280px">
            <VStack gap="2" textAlign="center" maxW="300px">
              <MessageSquare size={22} />
              <Text fontSize="sm" fontWeight="750">
                {showResolved ? 'No comments yet' : 'All clear'}
              </Text>
              <Text fontSize="xs" color="ui.text.secondary">
                {showResolved
                  ? 'Comments from every Living Doc will appear here.'
                  : 'There are no unresolved comments across your Docs & Assets.'}
              </Text>
            </VStack>
          </Center>
        )}
      </Box>
    </VStack>
  );
}
